import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'reactstrap';
import './CustomMultiSelect.css';

export interface MultiSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface CustomMultiSelectProps {
  options: MultiSelectOption[];
  value: (string | number)[];
  onChange: (selectedValues: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  maxSelectedLabels?: number;
  className?: string;
  style?: React.CSSProperties;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  value = [],
  onChange,
  maxSelectedLabels,
  placeholder = 'Select options...',
  disabled = false,
  searchable = false,
  clearable = false,
  className = '',
  style = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions =
    searchable && searchTerm
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : options;

  const selectedOptions = options.filter((option) =>
    value.includes(option.value),
  );

  const handleOptionToggle = (optionValue: string | number) => {
    if (disabled) return;

    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && clearable) {
      onChange([]);
    }
  };

  const renderSelectedLabels = () => {
    if (selectedOptions.length === 0) {
      return (
        <span className="custom-multiselect-placeholder">{placeholder}</span>
      );
    }

    if (
      maxSelectedLabels != null &&
      selectedOptions.length <= maxSelectedLabels
    ) {
      return (
        <div className="custom-multiselect-tags">
          {selectedOptions.map((option) => (
            <span key={option.value} className="custom-multiselect-tag">
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  className="custom-multiselect-tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionToggle(option.value);
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      );
    }

    return (
      <span className="custom-multiselect-count">
        {selectedOptions.length} items selected
      </span>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`custom-multiselect ${className} ${
        disabled ? 'disabled' : ''
      }`}
      style={style}
    >
      <div
        className={`custom-multiselect-control ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="custom-multiselect-value">{renderSelectedLabels()}</div>
        <div className="custom-multiselect-indicators">
          {clearable && selectedOptions.length > 0 && !disabled && (
            <button
              type="button"
              className="custom-multiselect-clear"
              onClick={handleClear}
            >
              ×
            </button>
          )}
          <span className={`custom-multiselect-arrow ${isOpen ? 'open' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="custom-multiselect-dropdown">
          {searchable && (
            <div className="custom-multiselect-search">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="custom-multiselect-options">
            {filteredOptions.length === 0 ? (
              <div className="custom-multiselect-no-options">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`custom-multiselect-option ${
                    value.includes(option.value) ? 'selected' : ''
                  } ${option.disabled ? 'disabled' : ''}`}
                  onClick={() =>
                    !option.disabled && handleOptionToggle(option.value)
                  }
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option.value)}
                    onChange={() => {}} // Handled by parent div click
                    disabled={option.disabled}
                  />
                  <span className="custom-multiselect-option-label">
                    {option.label}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomMultiSelect;
